// resolveTurnRoute.js — E1-WS2-T02 (refactor-deepening / F1, Composer/Send Flow seam).
//
// Deep module owning the TURN ROUTE RESOLUTION that ran inline near the top of
// SessionWorkspace.handleSend: pick the effective model (explicit pick → session model →
// active profile's model), optionally let the Turn Role Router swap in a cheaper model,
// then resolve the cloud/local route. Returns { effModel, routeDecision, route } — the
// triple the rest of handleSend (spec gate + runAgent + pendingSpecTurn stash) consumes.
//
// Deletion test (01-DOCTRINE): delete this and the model-pick + multi-profile identity +
// turn-router swap + detectRoute scatter back into handleSend. It concentrates the
// "which model/route does THIS turn use" decision behind one call. DEEP.
//
// Behavior byte-identical: same branches, same order (profile BEFORE router BEFORE
// detectRoute), same telemetry (observe 'role_route' only when the router actually swaps).
// PURE route helpers + the flag/stores are imported; the turn-specific + component state
// is injected.

import { routeTurn } from '../../../lib/turnRouter/turnRouter.js';
import { resolveRoutedModel } from '../../../lib/turnRouter/resolveRoutedModel.js';
import { detectRoute, buildAvailableModels } from './codeRouting.js';
import { DEFAULT_MODEL } from '../../../store/useAgentStore.js';
import { readMultiProfileEnabled } from '../../../lib/flags/sprintFlags.js';
import { useAuthStore } from '../../../store/useAuthStore.js';
import { decideAutoMode } from '../../../lib/turnRouter/autoMode.js';

/**
 * @param {{
 *   text: string, model: string | null, sessionModel: string,
 *   adapters: any[], effort: any,
 *   turnRouterEnabled: boolean, priorTurnTouchedCode: boolean,
 *   autoMode?: boolean, hasAttachments?: boolean,
 *   observe: (kind: string, payload: object) => void,
 * }} deps
 * @returns {{ effModel: string, routeDecision: object | null, route: object }}
 */
export function resolveTurnRoute({
  text, model, sessionModel, adapters, effort,
  turnRouterEnabled, priorTurnTouchedCode, observe, autoMode = false, hasAttachments = false,
}) {
  let effModel = model || sessionModel;
  // E3 (super-marathon) — "trocar de perfil acorda a identidade": an UNPINNED turn adopts
  // the active profile's model. Resolved HERE (code-ide layer) — never in the SACRED chat.js.
  if (readMultiProfileEnabled() && !model) {
    const { profiles, activeProfileId } = useAuthStore.getState();
    const activeProfile = (profiles || []).find((p) => p.id === activeProfileId);
    if (activeProfile?.model_id) effModel = activeProfile.model_id;
  }
  // Turn Role Router (opt-in, default OFF): for analysis/chat turns on the DEFAULT model,
  // swap in a cheaper available model. Applied BEFORE detectRoute so the route stays
  // consistent with the routed model. OFF ⇒ routeDecision null ⇒ identical path.
  let routeDecision = null;
  if (turnRouterEnabled && !autoMode) {
    routeDecision = routeTurn({ text, priorTurnTouchedCode });
    const routedId = resolveRoutedModel({
      modelTier: routeDecision.modelTier,
      currentModel: effModel,
      defaultModel: DEFAULT_MODEL,
      availableModels: buildAvailableModels(adapters),
    });
    if (routedId) { observe('role_route', { from: effModel, to: routedId, tier: routeDecision.modelTier }); effModel = routedId; } // C-13 telemetria passiva do downgrade
  }
  if (autoMode) {
    routeDecision = decideAutoMode({ text, currentModel: effModel, models: buildAvailableModels(adapters), priorTurnTouchedCode, hasAttachments });
    effModel = routeDecision.model;
    effort = routeDecision.effort;
    observe('auto_route', { tier: routeDecision.modelTier, model: effModel, effort });
  }
  const route = detectRoute(effModel, adapters, effort);
  return { effModel, routeDecision, route };
}
